import type { ContentPart } from "@/lib/chatStreamHelpers";
import { toolCardRegistry, type ToolCardRegistryKind } from "@/lib/toolCardRegistry";
import { ExperimentDesignSelectionResolveSchema } from "@/lib/toolCardSchemas/entitySelection";
import { parseCnsToolExecutionArtifact } from "@/lib/toolExecutionArtifact";
import { requiresActionRequestSubmission } from "@/lib/toolPresentationBehavior";

export type AssistantThreadStatus =
  | { type: "complete"; reason: "stop" }
  | { type: "requires-action"; reason: "tool-calls" }
  | { type: "incomplete"; reason?: string; error?: string }
  | { type: "running" };

/**
 * A real, renderable tool-call part. Presentation classification separately determines whether
 * the part represents a durable Action Request, an independent interaction, or a receipt.
 */
export function isRealHumanToolCallPart(
  part: ContentPart,
): part is ContentPart & { type: "tool-call"; toolCallId: string; toolName: string } {
  return part.type === "tool-call";
}

/** True when a tool-call part is waiting for user submission (no result yet). */
export function isPendingHumanToolCallPart(part: ContentPart): boolean {
  if (!isRealHumanToolCallPart(part)) {
    return false;
  }
  if (parseCnsToolExecutionArtifact(part.artifact)) {
    return false;
  }
  return (
    requiresActionRequestSubmission(part.toolName) &&
    (part.result === undefined || part.result === null)
  );
}

export function hasPendingHumanToolCalls(content: unknown): boolean {
  if (!Array.isArray(content)) {
    return false;
  }
  return content.some(isPendingHumanToolCallPart);
}

/**
 * Status to apply after an assistant stream ends. Keeps requires-action when
 * human tool cards still need input even if the SSE stream completed.
 */
export function resolveAssistantStatusAfterStream(content: unknown): AssistantThreadStatus {
  if (hasPendingHumanToolCalls(content)) {
    return { type: "requires-action", reason: "tool-calls" };
  }
  return { type: "complete", reason: "stop" };
}

/**
 * Human tool UIs should treat submission as complete when a result exists,
 * including an empty object from a no-field resolve schema.
 */
export function isHumanToolCallResolved(
  result: unknown,
  isError?: boolean,
): boolean {
  if (isError) {
    return false;
  }
  return result !== undefined && result !== null;
}

/** Applies a tool result to the matching tool-call part without mutating other parts. */
export function applyToolResultToContent(
  content: unknown,
  toolCallId: string,
  result: unknown,
  isError = false,
): ContentPart[] {
  if (!Array.isArray(content)) {
    return [];
  }
  return content.map((part: ContentPart) => {
    if (part.type === "tool-call" && part.toolCallId === toolCallId) {
      return {
        ...part,
        result,
        state: "result" as const,
        ...(isError ? { isError: true } : {}),
      };
    }
    return part;
  });
}

/**
 * Builds action-specific submission data. Catalog kinds and older selection tools
 * are reduced to their explicit human-decision schemas before persistence.
 */
export function sanitizeHumanToolDataForPost(
  toolName: string,
  result: Record<string, unknown>,
): Record<string, unknown> {
  if (toolName in toolCardRegistry) {
    const entry = toolCardRegistry[toolName as ToolCardRegistryKind];
    const toolData = { ...result };
    // assistant-ui needs this transport identity to route addResult; it is never part of
    // the backend-owned human decision contract.
    delete toolData.toolCallId;
    delete toolData.toolName;
    const parsed = entry.resolveSchema.safeParse(toolData);
    if (!parsed.success) {
      throw new Error("Tool result is invalid and was not sent.");
    }
    return parsed.data as Record<string, unknown>;
  }
  if (toolName === "experiment_design") {
    const parsed = ExperimentDesignSelectionResolveSchema.safeParse(result);
    if (!parsed.success) {
      throw new Error("Tool result is invalid and was not sent.");
    }
    const toolData: Record<string, unknown> = { ...parsed.data };
    delete toolData.toolCallId;
    return toolData;
  }
  const sanitized = { ...result };
  delete sanitized.toolCallId;
  delete sanitized.toolName;
  return sanitized;
}
